// Client mirror of functions-common/src/charts/engine/bucket.ts. Keep in sync.

import type { ChartTimeBucket } from '../types.js'

export function toDate(value: unknown): Date | null {
  if (value == null) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value === 'number') {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
  }
  if (typeof value === 'string') {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
  }
  if (typeof value === 'object') {
    const candidate = value as { seconds?: number; _seconds?: number }
    const s = candidate.seconds ?? candidate._seconds
    if (typeof s === 'number') {
      return new Date(s * 1000)
    }
  }
  return null
}

export interface ZonedDateParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
  millisecond: number
}

export function getZonedParts(date: Date, timeZone: string): ZonedDateParts {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  })
  const parts = formatter.formatToParts(date)
  const p: Record<string, number> = {}
  for (const part of parts) {
    if (part.type !== 'literal') {
      p[part.type] = Number(part.value)
    }
  }
  let hour = p.hour ?? 0
  if (hour === 24) hour = 0
  return {
    year: p.year ?? date.getUTCFullYear(),
    month: p.month ?? date.getUTCMonth() + 1,
    day: p.day ?? date.getUTCDate(),
    hour,
    minute: p.minute ?? 0,
    second: p.second ?? 0,
    millisecond: date.getUTCMilliseconds(),
  }
}

export function zonedTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  ms: number,
  timeZone: string
): Date {
  let guess = Date.UTC(year, month - 1, day, hour, minute, second, ms)
  for (let i = 0; i < 3; i++) {
    const p = getZonedParts(new Date(guess), timeZone)
    const pUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second, ms)
    const diff = pUtc - Date.UTC(year, month - 1, day, hour, minute, second, ms)
    if (diff === 0) break
    guess -= diff
  }
  return new Date(guess)
}

export function formatZonedDateKey(date: Date, timeZone: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    return formatter.format(date) // YYYY-MM-DD
  } catch {
    return date.toISOString().slice(0, 10)
  }
}

export function getWeekBucketKey(date: Date, timeZone: string): string {
  const dateStr = formatZonedDateKey(date, timeZone)
  const parts = dateStr.split('-').map(Number)
  const y = parts[0]
  const m = parts[1]
  const d = parts[2]
  // UTC anchor to calculate pure calendar days
  const target = new Date(Date.UTC(y, m - 1, d))
  const dayOfWeek = target.getUTCDay()
  const isoDay = dayOfWeek === 0 ? 7 : dayOfWeek
  target.setUTCDate(target.getUTCDate() + 4 - isoDay)
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1))
  const weekNo = Math.ceil(((target.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
  return `${target.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`
}

export function getMonthBucketKey(date: Date, timeZone: string): string {
  const dateStr = formatZonedDateKey(date, timeZone)
  return dateStr.slice(0, 7) // YYYY-MM
}

export function getQuarterBucketKey(date: Date, timeZone: string): string {
  const dateStr = formatZonedDateKey(date, timeZone)
  const year = dateStr.slice(0, 4)
  const month = Number(dateStr.slice(5, 7))
  const quarter = Math.floor((month - 1) / 3) + 1
  return `${year}-Q${quarter}`
}

export function bucketDate(date: Date, bucket: ChartTimeBucket, timeZone: string): string {
  switch (bucket) {
    case 'day':
      return formatZonedDateKey(date, timeZone)
    case 'week':
      return getWeekBucketKey(date, timeZone)
    case 'month':
      return getMonthBucketKey(date, timeZone)
    case 'quarter':
      return getQuarterBucketKey(date, timeZone)
    default:
      return formatZonedDateKey(date, timeZone)
  }
}

export function shiftCalendarDays(date: Date, days: number, timeZone: string): Date {
  const p = getZonedParts(date, timeZone)
  const anchor = Date.UTC(p.year, p.month - 1, p.day + days, 12, 0, 0, 0)
  const d = new Date(anchor)
  return zonedTimeToUtc(
    d.getUTCFullYear(),
    d.getUTCMonth() + 1,
    d.getUTCDate(),
    p.hour,
    p.minute,
    p.second,
    p.millisecond,
    timeZone
  )
}

export function shiftCalendarMonths(date: Date, months: number, timeZone: string): Date {
  const p = getZonedParts(date, timeZone)
  const totalMonths = p.year * 12 + (p.month - 1) + months
  const targetYear = Math.floor(totalMonths / 12)
  const targetMonth = ((totalMonths % 12) + 12) % 12 + 1
  const daysInMonth = new Date(Date.UTC(targetYear, targetMonth, 0)).getUTCDate()
  const targetDay = Math.min(p.day, daysInMonth)
  return zonedTimeToUtc(
    targetYear,
    targetMonth,
    targetDay,
    p.hour,
    p.minute,
    p.second,
    p.millisecond,
    timeZone
  )
}

export function getBucketSequence(
  from: Date,
  to: Date,
  bucket: ChartTimeBucket,
  timeZone: string
): string[] {
  const startKey = bucketDate(from, bucket, timeZone)
  const endKey = bucketDate(to, bucket, timeZone)

  if (startKey > endKey) return [startKey]

  const keys: string[] = []

  if (bucket === 'day') {
    let curr = from
    let currKey = formatZonedDateKey(curr, timeZone)
    while (currKey <= endKey) {
      keys.push(currKey)
      const p = getZonedParts(curr, timeZone)
      const nextAnchor = Date.UTC(p.year, p.month - 1, p.day + 1, 12, 0, 0, 0)
      const nd = new Date(nextAnchor)
      curr = zonedTimeToUtc(
        nd.getUTCFullYear(),
        nd.getUTCMonth() + 1,
        nd.getUTCDate(),
        12,
        0,
        0,
        0,
        timeZone
      )
      const nextKey = formatZonedDateKey(curr, timeZone)
      if (nextKey <= currKey) break // safeguard against infinite loop
      currKey = nextKey
    }
  } else if (bucket === 'week') {
    let curr = from
    let currKey = getWeekBucketKey(curr, timeZone)
    const seen = new Set<string>()
    while (currKey <= endKey) {
      if (!seen.has(currKey)) {
        seen.add(currKey)
        keys.push(currKey)
      }
      const p = getZonedParts(curr, timeZone)
      const nextAnchor = Date.UTC(p.year, p.month - 1, p.day + 7, 12, 0, 0, 0)
      const nd = new Date(nextAnchor)
      curr = zonedTimeToUtc(
        nd.getUTCFullYear(),
        nd.getUTCMonth() + 1,
        nd.getUTCDate(),
        12,
        0,
        0,
        0,
        timeZone
      )
      const nextKey = getWeekBucketKey(curr, timeZone)
      if (nextKey < currKey) break
      currKey = nextKey
    }
  } else if (bucket === 'month') {
    const startParts = startKey.split('-').map(Number)
    const endParts = endKey.split('-').map(Number)
    let y = startParts[0]
    let m = startParts[1]
    while (true) {
      const key = `${y}-${String(m).padStart(2, '0')}`
      keys.push(key)
      if (key >= endKey) break
      m++
      if (m > 12) {
        m = 1
        y++
      }
      if (y > endParts[0] + 1) break
    }
  } else if (bucket === 'quarter') {
    const startYear = Number(startKey.slice(0, 4))
    const startQ = Number(startKey.slice(6))
    const endYear = Number(endKey.slice(0, 4))
    const endQ = Number(endKey.slice(6))
    let y = startYear
    let q = startQ
    while (true) {
      const key = `${y}-Q${q}`
      keys.push(key)
      if (y > endYear || (y === endYear && q >= endQ)) break
      q++
      if (q > 4) {
        q = 1
        y++
      }
    }
  }

  return keys
}
